import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { AppointmentModel, EstimateCharge } from '@mmx/shared'
import { EstimateWarning } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { EstimateBalanceCalculationService } from '../../services/estimate-balance-calculation.service'

interface CocSummaryRow {
  visitNumber: number
  appointment: AppointmentModel | null
  appointmentCpts: string[]
  estimatorCpts: string[]
  dateLabel: string
  empty: boolean
}

@Component({
  selector: 'mmx-balance-charges-coc-summary',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, MatTooltipModule],
  templateUrl: './balance-charges-coc-summary.component.html',
  styleUrls: ['./balance-charges-coc-summary.component.scss'],
})
export class BalanceChargesCocSummaryComponent implements OnChanges {
  /** em-dash (not hyphen) — mirrors the i18n key `balance.charges.coc-summary-row-empty-charges`. */
  readonly EMPTY_PLACEHOLDER = '—'

  @Input() selectedAppointments: AppointmentModel[] = []
  @Input() charges: EstimateCharge[] = []
  @Input() unscheduledMode = false
  @Input() warnings: EstimateWarning[] = []

  // Precomputed in ngOnChanges — a getter would re-sort/re-derive into a fresh array each
  // CD pass, breaking *ngFor identity under OnPush.
  rows: CocSummaryRow[] = []

  constructor(private readonly calc: EstimateBalanceCalculationService) {}

  ngOnChanges(): void {
    this.rows = this.unscheduledMode ? this.unscheduledRows() : this.scheduledRows()
  }

  trackByVisitNumber(_index: number, row: CocSummaryRow): number {
    return row.visitNumber
  }

  private scheduledRows(): CocSummaryRow[] {
    const sorted = this.selectedAppointments
      .slice()
      .sort((a, b) => Number(a.startAt?.valueOf() ?? 0) - Number(b.startAt?.valueOf() ?? 0))
    return sorted.map((appointment, index) => {
      const visitNumber = index + 1
      return {
        visitNumber,
        appointment,
        appointmentCpts: this.calc.appointmentCptCodes(appointment),
        estimatorCpts: this.calc.estimatorCptCodesForVisit(this.charges, visitNumber),
        dateLabel: this.formatStartAt(appointment),
        empty: false,
      }
    })
  }

  private unscheduledRows(): CocSummaryRow[] {
    const n = this.calc.deriveUnscheduledVisitCount(this.charges)
    const warned = new Set((this.warnings ?? []).filter(w => w.code === 'EMPTY_VISIT').map(w => w.visitNumber))
    const rows: CocSummaryRow[] = []
    for (let i = 1; i <= n; i++) {
      const estimatorCpts = this.calc.estimatorCptCodesForVisit(this.charges, i)
      rows.push({
        visitNumber: i,
        appointment: null,
        appointmentCpts: [],
        estimatorCpts,
        dateLabel: '',
        empty: estimatorCpts.length === 0 || warned.has(i),
      })
    }
    return rows
  }

  // Inline format keeps the standalone component free of the momentDateTime pipe's
  // AuthService/ClinicService/HttpClient injectors. Authoritative formatting lives upstream.
  private formatStartAt(appointment: AppointmentModel): string {
    const startAt = appointment.startAt
    if (!startAt) {
      return ''
    }
    return typeof startAt.format === 'function' ? startAt.format('L LT') : String(startAt)
  }
}
